// ingest.video-serial task (§10.2). The single Gemini pass of the serial video
// pipeline: it watches ONE episode's video WITH the running cross-episode context
// (registry + plot digest + open threads) and returns ONE structured JSON object
// (responseMimeType application/json + VIDEO_SERIAL_SCHEMA) carrying both (a) the
// episode's source-faithful storyboard screenplay (scenes → visual/performance/sound anchors) and (b) incremental
// registry maintenance (new_entities / enrich / canonicalize) + open threads. This
// replaces the old "emit markdown → parseSerialMd" contract: structured output
// removes a whole fragile text-parsing layer and lets the schema forbid the outfit-
// name pollution (parens/slashes) at the source. Because it parses in order with
// full plot context, identity is resolved by story — reusing canonical names,
// registering anchored provisionals for unknowns, and canonicalizing an existing
// identity when direct written/spoken evidence reveals a better name.

import { CliError, EXIT_RUNTIME } from "../../../common.js";
import { isCrowdName } from "../../../domain/ingest/serial-context.js";
import { normalizeSceneTime } from "../../../domain/ingest/environment.js";
import { normalizeVideoTimestamp } from "../../../domain/ingest/video-timestamp.js";
import type { SerialOutput, SerialScene, SerialEntity, SerialIdentityProfile, SerialStateDescription } from "../../../domain/ingest/video-serial-md.js";
import { VIDEO_SERIAL_SCHEMA } from "../schemas.js";
import type { TaskVideoPort, VideoMediaInput } from "../../types.js";

export type { SerialOutput, SerialScene, SerialEntity } from "../../../domain/ingest/video-serial-md.js";

export const VIDEO_SERIAL_SYSTEM =
  "你是短剧视频转剧本的串行解析器。输入是**某一集**竖屏短剧视频（1-2 分钟）+ 一份到上一集为止累积的**已知设定**（人物花名册/剧情进展/未闭合线索）。" +
  "你要输出**一个结构化 JSON 对象**（严格符合系统给定的输出 schema），同时包含：①这一集尽量忠实还原原片的**分镜剧本**（scenes）；②对已知设定的**增量维护**（registry_delta：人物花名册带状态标注、规范名升级）+ 剧情线索（open_threads）。**只输出该 JSON，不要任何额外文字、解释或代码块包裹。**\n" +
  "核心要求：逐镜保留原片的可见信息、人物表演、站位关系、摄影调度与全部声音；动作/对白/实体准确，不脑补，时间戳对齐画面。\n" +
  "【时间戳逐条必填】每一条 shots 和 voices 都必须填写该镜头或声音真正开始的非空 ts，格式严格为 MM:SS（例如『00:03』）。不得留空、填 null、填未知值或省略字段；不确定时重新观看画面或听音轨，填写最接近的实际起点。任一条缺少合法 ts 时，整份 JSON 都不能提交。\n" +
  "scenes 必须包含本集实际可见的场景，至少一项；无新人物、无对白或没有设定增量时仍须完整记录本集画面，不能只返回梗概或 registry_delta，也不能用空 scenes 表示沿用上集设定。\n" +
  "【单次解析顺序——必须依次完成】第一步，从视频开头到结尾完整听一遍音轨，按时间顺序盘点所有可辨识人声，填入各 scene 的 voices；第二步，从开头到结尾逐镜盘点每次切镜、连续镜头内的摄影变化、画面主体、站位、动作、表情、视线、接触、道具和环境变化，填入各 scene 的 shots；第三步，分别复核 voices 是否完整、shots 是否逐镜覆盖全片。完成 JSON 前再次复核最后十秒，确认插入画面、手机/屏幕画面、回忆、蒙太奇、重复画面、反打和片尾前反应都没有漏写。盘点和复核只在内部完成，不另增输出字段。\n" +
  "【身份归属——本任务重点】带着已知设定看这一集：\n" +
  "- 出现的人先判断**是不是花名册里已有的某个人**：优先比对多条 recognition_anchors 和 identity_profile 中跨造型稳定的脸部结构、固定标记、发际线/常态发型、骨架体型与声线；服装、妆容和当下姿态只作弱辅助，role 只提供角色语境，不能替代视觉证据。稳定特征一致才沿用其**规范名**。若关键稳定特征冲突，不要强行合并，保留或登记独立 provisional 身份等待直接证据。\n" +
  "- 认不出、也没听到本名的人 ⇒ **不要硬猜、不要编带姓的假名**。用『稳定外观特征+年龄段+性别』构成不含姓氏的描述名，status 标 `provisional`，仍需填写完整可见身份画像、识别锚点与作用。\n" +
  "- 描述名、简称或不完整姓名只有在本集出现**书面正式姓名或台词直接说出本名**时才能升级规范名；在 registry_delta.canonicalize 写 {from_name, canonical_name, evidence}，evidence 必须写明时间戳和当时可见的原文或听到的原话。剧情关系只能帮助判断是不是同一个人，不能单独作为改名证据。\n" +
  "- 完成前执行资产对账：汇总 scenes.location、scenes.characters、scenes.props 与人物 voices.speaker 中出现的每个资产名，逐项确认它能解析到已知设定、registry_delta.new_entities 或本集 canonicalize 的目标。任何未登记人物必须补入 new_entities 并填写 identity_profile、recognition_anchors、role 与 states；未登记地点/道具必须填写 appearance、role 与 states。若一个物件不值得成为关键道具资产，就从 scenes.props 移除。对账差集非空时不得输出 JSON。规范名升级后立即使用 canonical_name，不让正文资产名脱离花名册。\n" +
  "【人物身份画像】首次登记人物时，从全片多个清晰角度综合填写 identity_profile，而不是用一句笼统外貌概括。逐项记录可见性别与年龄段、脸型/颧骨/下颌、眉眼、鼻部、静态唇形与口周、稳定肤色和面部特征、稳定发色/发际线/常态发型、骨架体型、固定身体标记；声线仅依据清晰原声填写。看不清的字段留空，不猜测。描述使用可观察的形状、比例、位置、颜色与纹理，避免审美、气质、性格、情绪和剧情评价。服饰、妆容、可摘饰品、当下表情与视线、动作、姿态、临时伤痕/血迹/污渍、变身或其他阶段性形态属于 states 或 shots，不混入身份画像。填写完逐字段检查：假设人物换装、卸妆、摘掉配饰、改变表情并进入另一场景，该描述是否仍成立；不成立就从 identity_profile 移出。\n" +
  "【识别锚点】人物 recognition_anchors 写 3-6 条最适合跨集重认的组合特征；每条至少组合两个来自 identity_profile 的稳定、可区分线索，并尽量覆盖脸部结构、五官比例、固定身体标记、发际线/常态头发、骨架体型或声线等不同维度。锚点不得包含服装、制服号码、帽子、眼镜或其他可摘配饰、妆容、当下神情/动作/姿态、临时伤痕污渍、剧情身份/能力/关系、变身形态或随场景变化的物件。已有实体在本集出现更清晰角度时，用 enrich.identity_profile_patch 只补充或修正有新证据的字段，并用 add_recognition_anchors 增加新锚点；不要把整段旧画像改写成另一句。输出前逐条核对：锚点中的每个词都能在 identity_profile 找到稳定依据，否则重写或删除。\n" +
  "【地点、道具与状态描述】地点 appearance 写空间结构、材质、色彩和固定陈设，role 写空间用途；道具 appearance 写形制、材质、颜色和尺寸关系，role 写用途。states 的每项都必须同时给出可复现的非空 description。人物造型先综合本集多个清晰镜头，再按从头到脚的稳定层次描述：该造型特有的发型与头饰、妆容、颈部饰物、外套、上装、下装或连体衣裙、袜类、鞋履、手套及其他饰品；每件看清的单品写明颜色、材质/光泽/纹理、版型与松紧、领型、袖型、开合、腰线、长度、下摆、图案、文字标识、滚边或金属件，并写清内外叠穿和配饰所在位置；持续破损、血迹、污渍或变形写明部位、范围与颜色。只省略画面未显示或无法确认的项，不用『穿着某服装』一句带过，不凭局部镜头补全全身。地点和道具状态写相对常态的持续外观差异。所有描述只写画面事实，不写动作、姿态、情绪、心理或剧情评价。已知设定中已有的状态沿用其 name；本集看到更多角度或更清晰细节时，用 enrich.add_states 的同名项补全 description。\n" +
  "【说话人——用剧情锁定，别只看脸】短剧常有几个长相相似的中老年男性高管，单帧人脸会认错。先从台词内容、彼此称呼（谁叫谁「哥」「X总」）、场景关系（谁求谁、谁训谁、谁下跪）推断这场在场是谁、谁对谁说，再定说话人。剧情锁定优先于外貌。\n" +
  "【命名优先级】书面正式姓名（人物介绍条/工牌/来电显示）> 台词喊出的本名 > 纯描述名。有书面正式姓名就用它当规范名，哪怕全集只喊职务。绝不凭空捏造带姓氏的假名。\n" +
  "【规范名唯一性】人物规范名只写姓名或稳定描述名，角色职能、关系、备注不得塞进括号。输出前按去空格、统一全半角括号并忽略括号备注后的可见名称检查花名册；两个不同人物不得得到会被归一成同名的规范名。若台词只给出相同称呼，保留已知人物原名，并给新人物使用能区分身份且不带括号的稳定描述名，例如『媒婆王婶』，角色说明继续写在 role。\n" +
  "【声音完整性】每段可辨识的人声都必须在 voices 中出现且只出现一次，包括人物出镜对白、画外对白、角色内心独白/自述、客观旁白、电话/广播/设备声、耳语、群体声和跨画面连续讲述。说话时画面切换、人物未出镜、只显示流程图/地图/手机/屏幕、进入回忆或蒙太奇、重复某段画面、口型不可见，都不是省略声音的理由。连续讲述跨 scene 或跨镜时按自然语句在对应时间点拆分，原话完整保留、不重不漏；用同语言对白字幕逐句核对音轨，尤其检查短句和插入画面上的连续声音。\n" +
  "【原声语言铁律】voices.content 逐字保留音轨实际使用的语言、文字和夹杂语言，不翻译、不改写、不用中文字幕替换外语原声。英文音轨配中文字幕时写英文原词；印尼语、泰语或其他语言音轨写对应语言原词；同一句发生 code-switch 时按听到的顺序原样保留。证据优先级是原始音轨 > 与音轨同语言的对白字幕 > 翻译字幕。字幕只能辅助辨清同语言原词，不能改变台词语言；听不清时保留可确认部分并在 parenthetical 标『听辨待复核』，不要用翻译字幕猜台词。\n" +
  "【画面文字与嵌套画面】底部对白字幕是声音台词的冗余证据，不单独建 shot；用它逐句核对 voices 是否完整。承载新信息的画面文字（人物介绍条/身份头衔/时间地点交代/系统提示/关键道具上的字）写入对应 shot.visual。手机、监控、屏幕、照片和回忆画面只写实际可见的人物动作、物体和可读文字；没有画面、台词或文字直接证明时，不推断人物身体状态、事件原因、前后关系或结果。\n" +
  "【状态=造型】state / states = 一个角色一整套稳定扮相（着装+发型+持续妆造，如「深灰西装」），不是情绪/姿态，也不是单件衣服。给某角色标造型前，先看已知设定里他名下的「已知造型」清单——只要本集是他已列过的某套扮相（哪怕只拍到上半身/换了角度/光线不同/多看到少看到一件单品），**逐字照抄清单里那个造型名**，只有整套换装才新建。造型名只填简洁档位名，详细组成全部写进 state.description。输出前复核每个造型：画面看清的服装层次、版型结构、材质配色、鞋袜、妆发和配饰是否已覆盖；若更清晰镜头能补充既有造型，就更新同名 description，不另建近义造型。\n" +
  // Retired experiment: Gemini previously selected identity_evidence and
  // state_evidence timestamps in this same response. The post-ingest evidence
  // pass now derives candidates from stable actors/states and Action timestamps,
  // avoiding extra output tokens and keeping evidence rebuildable without LLMs.
  "【场界与地点】一场是地点、连续时段和叙事线连续的生产单元。客厅、卧室、卫生间、大厅、广场、舞台、后台分别使用独立 location；相关空间统一命名为『豪宅·客厅』『豪宅·卧室』。人物跨过门槛进入另一空间、叙事进入另一稳定空间、明显跳时、切换叙事线或明确转场时建立新 scene。一个 scene 只写一个主 location；同一连续场内的人物造型变化不是分场理由。\n" +
  "【状态变化与场内资产】人物换整套服装、变身、脱衣形成可持续新外观时，把变化动作写入当前 scene 的 shots。characters 中同一规范名只出现一次；该条的 states 是本场出现过的全部稳定造型集合，不能漏掉变化前或变化后状态，也不按先后拆成重复人物。地点被毁或关键道具明显变形时登记对应新 state。情绪、姿态和一次性动作保留在 shots 的 visual，不建立 state。\n" +
  "【逐镜还原】shots 是独立分镜清单。每次切镜都在起始时间新增一条 shot，即使它重复展示之前的动作或没有新剧情，也要保留。连续镜头内的景别、拍摄角度、机位、镜头移动、构图、焦点或画面主体变化，以及每个新动作、反应、接触/分开、道具操作与结果、进出场、移动、姿势/朝向/视线或空间关系变化，都在实际发生时另增 shot。不得把先后发生的分镜或动作压缩成剧情概述。\n" +
  "【shot.camera】只写原片可见、可复现的摄影信息：远景/全景/中景/近景/特写等景别，俯拍/仰拍/平视/侧面/背面等角度，固定、推、拉、摇、移、跟、环绕、升降、焦点转移等可确认的运镜，以及前景/中景/后景、画面左右、遮挡和主体占位。切镜、反打、插入镜头、主观镜头、空镜和重复镜头都如实保留。不推测画面无法确认的镜头参数、镜头型号、灯光设置或拍摄意图。\n" +
  "【站位与持续状态】每场第一个 shot 的 visual 建立画面可确认的人物站位/坐卧位置、与地点固定参照物的关系、人物之间的远近与前后左右、姿态、面向、视线、接触和正在进行的行为。后续切镜若显示新的构图关系，写清人物在画面左/右、前景/中景/后景的位置；若人物进出、移动、转身、改变姿势、注视对象、接触或分开，写清变化前后的关系。持续状态不因对白而失效，但每次切镜仍需写该镜头实际呈现的构图和主体。\n" +
  "【逐句对白与同期表演对齐——本任务完成门】为每条 voice 单独回看该句开始前到说完的画面，确认说话者和同场人物当时的可见动作、表情、视线、姿态、接触和位置关系已在 shots 中准确承载。与这句话同时发生、不独立改变连续性的短促表演可写入该 voice 的 parenthetical；走位、姿势变化、接触/分开、道具操作或空间关系变化写入对应时间的 shot.visual。不得让对白时间点将正在发生的可见表演变成对白之后的动作。\n" +
  "【shot.visual】使用人物规范名，写清行动者、动作、作用对象、空间方向和可见结果。只写具体可见的眉眼、嘴部、呼吸、视线、手势、姿态、接触、移动、道具和环境变化，不用愤怒、焦虑、委屈、绝望、震惊、哀求、嘲讽等情绪、态度或心理判断代替画面事实。只写原片能够确认的事实；看不清的位置、朝向、表情或动作不写，不补造。synopsis 可以概括剧情，shots 不得写成剧情梗概。\n" +
  "【正文与造型】角色的既有服装由 characters.states 和人物资产承载，shot.visual 不重复描述。只有画面中发生的换装、脱衣、服装破损、沾染泥污等可见变化，或确有必要区分人物/时段时才写入 shot.visual。\n" +
  "【voices】人物对他人说出的声音写 kind=dlg；音轨里实际听到、明确属于某角色的内心独白或第一人称自述写 kind=think。speaker 使用角色规范名；客观叙述者或身份无法确认的旁白写 speaker=『broadcast:旁白』；电话、广播或设备中的明确说话人在 parenthetical 标明声源。只记录音轨实际表达的内心，不脑补未说出的心理。content 保留原话；parenthetical 可写可确认的发声方式、声音来源或同期短促表演，但摄影调度和影响连续性的动作只写 shots。对白跨越切镜、镜头主体变化或独立动作时，在对应时间点按原声自然语句拆分；不得把跨多个镜头的多句台词合并成一条。两人同时说话时输出相邻两条 voice，第二条 parenthetical 以『同时』开头。具体单人使用规范名；无法区分成员的众人、群众、路人等共同发声时 speaker 写『group:群体名』，不列入 characters 和 registry_delta。\n" +
  "【蒙太奇】同地点连续蒙太奇保留一场；跨地点蒙太奇按剪辑顺序拆成多个短 scene，每场绑定自己的 location、characters、props 和 state，并用 shots 明确蒙太奇开始、切换和结束。";

export function buildSerialUser(episodeNo: number, contextText: string): string {
  const ctx = contextText.trim()
    ? `========== 已知设定（到上一集为止，规范名以此为准） ==========\n${contextText}\n`
    : "（这是第一集，暂无已知设定。）\n";
  return `解析第 ${episodeNo} 集：对照本集原视频，按输出 schema 返回 JSON 对象。scenes 逐场覆盖全集；每场先独立完成逐镜 shots，再独立完成 voices。shots 必须覆盖每次切镜和可确认的摄影/表演变化，不去重重复镜头，不合并先后发生的分镜或动作；voices 完整保留原声并在跨镜时按自然语句拆分。registry_delta 只报增量（已知且无新信息的实体不要写）。完成前分别复核 shots 的切镜、景别/角度/运镜/构图/焦点、可见表演和站位关系，以及 voices 的全部人声与视频最后十秒。\n\n${ctx}`;
}

function serialError(message: string, received: string[]): CliError {
  return new CliError(message, {
    exitCode: EXIT_RUNTIME,
    errorCode: "INGEST_VIDEO_SERIAL_FAILED",
    hint: "Inspect the episode error sidecar and rerun `scriptctl ingest` to resume.",
    data: { actual: received },
  });
}

// Gemini occasionally emits a NUL () inside a string (e.g. a garbled dialogue
// line). It's valid JSON but Postgres text/jsonb can't store it, so a downstream
// insert of script.json dies with UntranslatableCharacter. NUL is never real content
// here — strip it at the source so every consumer stays safe, not just this one DB.
// (Only NUL: other control chars store fine, and touching surrogates would corrupt
// legit non-BMP CJK that arrives as valid surrogate pairs.)
const stripNul = (parsed: unknown): unknown => {
  if (typeof parsed === "string") return parsed.replace(/\u0000/g, "");
  if (Array.isArray(parsed)) return parsed.map(stripNul);
  if (isObj(parsed)) return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, stripNul(v)]));
  return parsed;
};

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const asStr = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

// Deterministic backstop for outfit-name pollution the schema description already
// forbids but a model may still leak: a state must be a bare wardrobe label. Strip
// everything from the first paren/slash/comma separator on — `黑丝短裙（身穿收银短袖）`
// and `黑丝短裙/绷带短裙` both collapse to `黑丝短裙`, folding the ep39/40/17-style
// fragments back onto the canonical outfit. Empty stays empty (no change this scene).
const cleanState = (raw: unknown): string => {
  const s = asStr(raw);
  return s ? s.split(/[（(／/，,、]/)[0]!.trim() : "";
};

const cleanStates = (raw: unknown): string[] => [...new Set(
  asArr(raw).map(cleanState).filter(Boolean),
)];

const cleanStateDescriptions = (raw: unknown): SerialStateDescription[] => {
  const states: SerialStateDescription[] = [];
  for (const value of asArr(raw)) {
    if (!isObj(value)) continue;
    const name = cleanState(value.name);
    if (!name) continue;
    const description = asStr(value.description);
    const existing = states.find((state) => state.name === name);
    if (existing) {
      if (!existing.description && description) existing.description = description;
    } else {
      states.push({ name, description });
    }
  }
  return states;
};

const IDENTITY_TEXT_KEYS = [
  "apparent_gender",
  "apparent_age",
  "face_shape",
  "brows_eyes",
  "nose",
  "mouth",
  "skin_features",
  "hair",
  "build",
  "voice",
] as const;

const cleanIdentityProfile = (raw: unknown): SerialIdentityProfile | undefined => {
  if (!isObj(raw)) return undefined;
  const profile: SerialIdentityProfile = {
    apparent_gender: "",
    apparent_age: "",
    face_shape: "",
    brows_eyes: "",
    nose: "",
    mouth: "",
    skin_features: "",
    hair: "",
    build: "",
    persistent_marks: asArr(raw.persistent_marks).map(asStr).filter(Boolean),
    voice: "",
  };
  for (const key of IDENTITY_TEXT_KEYS) profile[key] = asStr(raw[key]);
  return profile;
};

const timestampSeconds = (timestamp: string | undefined): number => {
  if (!timestamp) return Number.POSITIVE_INFINITY;
  const parts = timestamp.split(":").map(Number);
  if (parts.some((part) => !Number.isFinite(part))) return Number.POSITIVE_INFINITY;
  if (parts.length === 2) return parts[0]! * 60 + parts[1]!;
  if (parts.length === 3) return parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  return Number.POSITIVE_INFINITY;
};

const shotContent = (camera: string, visual: string): string => {
  if (!camera) return visual;
  if (!visual) return camera;
  return `${camera.replace(/[。！？；]+$/, "")}。${visual}`;
};

// Structured-output JSON → SerialOutput. The schema guarantees shape; this is a
// defensive projection (tolerates missing/wrong-typed fields) that also runs every
// state through cleanState and re-nests space/time into `environment` the way
// applyDelta / deltaToDoc expect.
export function toSerialOutput(input: unknown): SerialOutput {
  const out: SerialOutput = { synopsis: "", scenes: [], registry_delta: { new_entities: [], enrich: [], canonicalize: [] }, open_threads: { opened: [], closed: [] } };
  if (!isObj(input)) return out;
  out.synopsis = asStr(input.synopsis);

  for (const s of asArr(input.scenes)) {
    if (!isObj(s)) continue;
    const environment: { space?: string; time?: string } = {};
    const space = asStr(s.space); if (space) environment.space = space;
    const time = normalizeSceneTime(s.time); if (time) environment.time = time;
    const characters = new Map<string, SerialScene["characters"][number]>();
    for (const c of asArr(s.characters)) {
      if (!isObj(c)) continue;
      const name = asStr(c.name);
      if (!name || isCrowdName(name)) continue;
      const character = characters.get(name) ?? { name, states: [] };
      for (const state of cleanStates(c.states)) {
        if (!character.states.includes(state)) character.states.push(state);
      }
      // Retired experiment: evidence fields from Gemini were normalized here.
      // They are intentionally ignored now; final Action timestamps drive the
      // post-ingest evidence pass.
      characters.set(name, character);
    }
    // Props are presence-only (no multi-state refs). Collapse exact/whitespace
    // duplicates the same way characters merge by name — otherwise finalize's
    // v3 gate rejects `duplicate prop ref` before v4 can fold them.
    const props: string[] = [];
    const seenProps = new Set<string>();
    for (const raw of asArr(s.props).map(asStr).filter(Boolean)) {
      const key = raw.normalize("NFC").replace(/\s+/g, " ").trim();
      if (!key || seenProps.has(key)) continue;
      seenProps.add(key);
      props.push(raw.trim());
    }
    const anchors: SerialScene["anchors"] = [];
    for (const shot of asArr(s.shots)) {
      if (!isObj(shot)) continue;
      const content = shotContent(asStr(shot.camera), asStr(shot.visual));
      if (!content) continue;
      const anchor: SerialScene["anchors"][number] = { kind: "act", content };
      const ts = normalizeVideoTimestamp(shot.ts); if (ts) anchor.ts = ts;
      anchors.push(anchor);
    }
    for (const voice of asArr(s.voices)) {
      if (!isObj(voice)) continue;
      const kind = asStr(voice.kind);
      if (kind !== "dlg" && kind !== "think") continue;
      const content = asStr(voice.content);
      if (!content) continue;
      const anchor: SerialScene["anchors"][number] = { kind, content };
      const speaker = asStr(voice.speaker); if (speaker) anchor.speaker = speaker;
      const parenthetical = asStr(voice.parenthetical); if (parenthetical) anchor.parenthetical = parenthetical;
      const ts = normalizeVideoTimestamp(voice.ts); if (ts) anchor.ts = ts;
      anchors.push(anchor);
    }
    anchors.sort((left, right) => {
      const timeDiff = timestampSeconds(left.ts) - timestampSeconds(right.ts);
      if (timeDiff !== 0) return timeDiff;
      if (left.kind === "act" && right.kind !== "act") return -1;
      if (left.kind !== "act" && right.kind === "act") return 1;
      return 0;
    });
    const scene: SerialScene = {
      environment,
      characters: [...characters.values()],
      props,
      anchors,
    };
    const timeRange = asStr(s.timeRange); if (timeRange) scene.timeRange = timeRange;
    const location = asStr(s.location); if (location) scene.location = location;
    out.scenes.push(scene);
  }

  const rd: Record<string, unknown> = isObj(input.registry_delta) ? input.registry_delta : {};
  for (const e of asArr(rd.new_entities)) {
    if (!isObj(e)) continue;
    const name = asStr(e.name); const kind = asStr(e.kind);
    if (!name || (kind !== "actor" && kind !== "location" && kind !== "prop")) continue;
    if (kind === "actor" && isCrowdName(name)) continue;
    const ent: SerialEntity = {
      name,
      kind,
      status: asStr(e.status) === "provisional" ? "provisional" : "canonical",
      importance: asStr(e.importance) === "background" ? "background" : "featured",
    };
    const recognitionAnchors = asArr(e.recognition_anchors).map(asStr).filter(Boolean);
    if (recognitionAnchors.length) ent.recognition_anchors = recognitionAnchors;
    const identityProfile = cleanIdentityProfile(e.identity_profile);
    if (identityProfile) ent.identity_profile = identityProfile;
    const appearance = asStr(e.appearance); if (appearance) ent.appearance = appearance;
    const role = asStr(e.role); if (role) ent.role = role;
    const states = cleanStateDescriptions(e.states); if (states.length) ent.states = states;
    const aliases = asArr(e.aliases).map(asStr).filter(Boolean); if (aliases.length) ent.aliases = aliases;
    out.registry_delta.new_entities.push(ent);
  }
  for (const e of asArr(rd.enrich)) {
    if (!isObj(e)) continue;
    const name = asStr(e.name); if (!name) continue;
    const en: SerialOutput["registry_delta"]["enrich"][number] = { name };
    const recognitionAnchors = asArr(e.add_recognition_anchors).map(asStr).filter(Boolean);
    if (recognitionAnchors.length) en.add_recognition_anchors = recognitionAnchors;
    const identityProfile = cleanIdentityProfile(e.identity_profile_patch);
    if (identityProfile) en.identity_profile_patch = identityProfile;
    const appearance = asStr(e.appearance); if (appearance) en.appearance = appearance;
    const role = asStr(e.role); if (role) en.role = role;
    const addA = asArr(e.add_aliases).map(asStr).filter(Boolean); if (addA.length) en.add_aliases = addA;
    const addS = cleanStateDescriptions(e.add_states); if (addS.length) en.add_states = addS;
    if (Object.keys(en).length > 1) out.registry_delta.enrich.push(en);
  }
  for (const r of asArr(rd.canonicalize)) {
    if (!isObj(r)) continue;
    const from = asStr(r.from_name); const canonical = asStr(r.canonical_name);
    if (from && canonical) out.registry_delta.canonicalize.push({ from_name: from, canonical_name: canonical, evidence: asStr(r.evidence) });
  }

  const ot: Record<string, unknown> = isObj(input.open_threads) ? input.open_threads : {};
  for (const o of asArr(ot.opened)) { if (!isObj(o)) continue; const text = asStr(o.text); if (text) out.open_threads.opened.push({ text }); }
  for (const c of asArr(ot.closed)) { if (!isObj(c)) continue; const ref = asStr(c.ref); if (ref) out.open_threads.closed.push({ ref }); }

  return out;
}

// Returns the raw JSON text (pretty-printed, persisted for human review) alongside
// the parsed SerialOutput the pipeline folds into context.
export interface VideoSerialResult {
  md: string;
  output: SerialOutput;
}

export async function runVideoSerial(
  port: TaskVideoPort,
  media: VideoMediaInput,
  episodeNo: number,
  contextText: string,
  fps: number,
  signal?: AbortSignal,
  local?: { path: string; mime: string },
): Promise<VideoSerialResult> {
  const response = await port.generate({
    media,
    system: VIDEO_SERIAL_SYSTEM,
    user: buildSerialUser(episodeNo, contextText),
    fps,
    responseJsonSchema: VIDEO_SERIAL_SCHEMA,
    // Lets a cross-provider fallback (Doubao) re-upload the same transcode when the
    // Gemini handle can't be reused. The Gemini path ignores these.
    ...(local ? { localPath: local.path, localMime: local.mime } : {}),
    ...(signal ? { signal } : {}),
  });
  const raw = response.text.trim();
  // A too-short body means a failed/empty generation (§12.3: not retried).
  if (raw.length < 40) throw serialError(`episode ${episodeNo}: serial output too short (finishReason=${response.finishReason})`, [`length: ${raw.length}`]);
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw serialError(`episode ${episodeNo}: serial output was not valid JSON (finishReason=${response.finishReason})`, [raw.slice(0, 200)]);
  }
  const clean = stripNul(parsed);
  const output = toSerialOutput(clean);
  if (output.scenes.length === 0) throw serialError(`episode ${episodeNo}: serial output produced no scenes`, [raw.slice(0, 200)]);
  const md = JSON.stringify(clean, null, 2);
  return { md, output };
}
